import type { LogEntry } from '@chipless/shared';
import { Badge, Group, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { useEffect, useRef } from 'react';

/**
 * 直近のアクション履歴を時系列で表示する（要件11.8）。
 *
 * 高さは置き場所が決め（横向きは画面の 3割、縦向きは残りの高さ）、中でスクロールさせる。
 * 行数に応じて伸ばすと、ハンドが進むにつれて中央ディスプレイ全体がスクロールを要する
 * 画面になる。卓から目視する用途ではスクロール位置で見える内容が変わるのが最も困るため、
 * 外側の高さは動かさない。
 */
export function ActionLog({ log }: { log: readonly LogEntry[] }): React.ReactElement {
  const viewport = useRef<HTMLDivElement>(null);

  // 末尾が常に見えるようにする。
  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight });
  }, [log]);

  return (
    <Paper withBorder p="sm" radius="md" h="100%" data-testid="action-log">
      <Stack gap="xs" h="100%">
        <Text size="sm" fw={700} c="dimmed">
          履歴
        </Text>
        {/* flex アイテムは既定で縮まないため min-height を 0 にする。これが無いと
            ScrollArea が中身の高さまで伸びて 30vh を超える。 */}
        <ScrollArea viewportRef={viewport} style={{ flex: 1, minHeight: 0 }} type="auto">
          <Stack gap={4}>
            {log.length === 0 && (
              <Text size="sm" c="dimmed">
                まだ記録はありません
              </Text>
            )}
            {log.map((entry) => (
              <Group key={entry.seq} gap={6} wrap="nowrap" align="baseline">
                <Text size="sm" style={{ flex: 1 }}>
                  {entry.text}
                </Text>
                {entry.proxy === true && (
                  <Badge size="xs" color="grape" variant="light">
                    代理
                  </Badge>
                )}
              </Group>
            ))}
          </Stack>
        </ScrollArea>
      </Stack>
    </Paper>
  );
}
